import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Button,
  Checkbox,
  CustomTooltip,
  Field,
  FileUploader,
  Input,
  Select,
  Typography,
} from '../../../../../components';
import { IconClose } from '../../../../../icons';
import { getRemoteConfigValue } from '../../../../../imports/remoteConfig';
import { renameFile } from '../../../../../imports/utils';
import { CustomFieldsProps } from '../../../imports/types';
import { isFileTypeImage } from '../../../imports/utils';
import { useAppSelector } from '../../../../../redux/hooks';
import i18n from '../../../../../i18n';

const DocsCustomField = ({
  errors,
  category,
  index,
  values,
  // customLabels,
  fieldLabel,
  resetField,
  fieldValue,
  setValue,
  fieldLink,
  fieldType,
  fieldPrivate,
  options,
}: CustomFieldsProps) => {
  const { t } = useTranslation(['tokenCreator']);
  const template = useAppSelector((state) => state.template);
  const language = i18n.language;
  const { nfts } = values;
  const attributes = values.nfts[category].attributes;
  const [isImageValid, setIsImageValid] = useState<boolean>(true);
  const allowedFileType: { [key: string]: string[] } = getRemoteConfigValue('allowedFiles', 'json');
  const attributeType = nfts?.[category]?.attributes?.[index]?.type;
  const attributeCustomType = nfts?.[category]?.attributes?.[index]?.customType;
  const templateField = template.customFields.find(
    (field: any) => field.value === attributeCustomType
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row gap-2 ">
        <Field
          error={errors.nfts?.[category]?.attributes?.[index]?.type?.message}
          label={t('create_contract.form_labels.attribute')}
          className="!w-1/2"
        >
          <Select
            options={options}
            error={errors.nfts?.[category]?.attributes?.[index]?.type?.message}
            name={fieldType.name}
            // onBlur={fieldType.onBlur}
            onChange={(change) => {
              const customFieldFound = template.customFields.find(
                (field: any) => field.value === change.target.value
              );
              setValue(`nfts[${category}].attributes[${index}].customType`, change.target.value);
              setValue(`nfts[${category}].attributes[${index}].type`, customFieldFound?.type);

              if (customFieldFound?.fields.trait_type?.value) {
                setValue(
                  `nfts[${category}].attributes[${index}].trait_type`,
                  customFieldFound?.fields.trait_type?.value
                );
              } else {
                setValue(`nfts[${category}].attributes[${index}].trait_type`, '');
              }
            }}
            // inputRef={fieldType.ref}
            inputRef={attributeType.ref}
            defaultValue={
              options.find(
                (option) => option.value === nfts?.[category]?.attributes?.[index]?.customType
              )?.value
            }
          />
        </Field>
        <div className="mb-2 flex w-1/2 items-end justify-between">
          <div className="flex items-center">
            <Typography>{t('create_contract.form_labels.private_field') as string}</Typography>
            <CustomTooltip>
              <Typography size="xs">
                {t('create_contract.form_tooltips.private_field') as string}
              </Typography>
            </CustomTooltip>
          </div>

          <Checkbox
            name={fieldPrivate.name}
            id={fieldPrivate.name}
            onChange={fieldPrivate.onChange}
            inputRef={fieldPrivate.ref}
            onBlur={fieldPrivate.onBlur}
            isChecked={values.nfts?.[category]?.attributes?.[index]?.private}
          />
        </div>
      </div>
      {/* SECOND ROW */}
      <div className="flex flex-col">
        <div className="flex gap-2">
          <Field
            error={errors.nfts?.[category]?.attributes?.[index]?.trait_type?.message}
            // label={
            //   attributes[index].type === 'document'
            //     ? template.customFields.document.fields.trait_type.label?.[language]
            //     : attributes[index].type === 'warranty_certificate'
            //     ? template.customFields.warranty_certificate.fields.trait_type.label?.[language]
            //     :
            // }
            label={templateField?.fields.trait_type?.label?.[language]}
            className="!w-1/2"
          >
            <Input
              type="text"
              placeholder={t('create_contract.form_placeholders.custom_field_label')}
              name={fieldLabel.name}
              onBlur={fieldLabel.onBlur}
              onChange={fieldLabel.onChange}
              inputRef={fieldLabel.ref}
              error={errors.nfts?.[category]?.attributes?.[index]?.trait_type?.message}
              elementRight={
                <Button type="ghost" icon={IconClose} action={() => resetField(fieldLabel.name)} />
              }
              // disabled={
              //   attributes[index].type === 'document'
              //     ? template.customFields.document.fields.trait_type.disabled
              //     : attributes[index].type === 'warranty_certificate'
              //     ? template.customFields.warranty_certificate.fields.trait_type.disabled
              //     : template.customFields.authenticity_certificate.fields.trait_type.disabled
              // }
              disabled={templateField?.fields.trait_type?.disabled}
            />
          </Field>
        </div>
      </div>
      {/* THIRD ROW */}

      <Typography size="body-regular-16" className="mt-4" weight="medium">
        {attributes[index].type === 'document'
          ? t('create_contract.form_labels.load_document')
          : t('create_contract.form_labels.load_certificate')}
      </Typography>

      <div className="flex flex-col gap-2">
        <Field
          error={
            !nfts?.[category]?.attributes?.[index]?.file?.[0] &&
            errors.nfts?.[category]?.attributes?.[index]?.link?.message
          }
          // label={
          //   attributes[index].type === 'document'
          //     ? t('create_contract.form_labels.document_link')
          //     : t('create_contract.form_labels.certificate_link')
          // }
          label={templateField?.fields.link?.label?.[language]}
        >
          <Input
            type="text"
            placeholder={t('create_contract.form_placeholders.link')}
            name={fieldLink.name}
            onBlur={fieldLink.onBlur}
            onChange={fieldLink.onChange}
            inputRef={fieldLink.ref}
            elementRight={
              <Button type="ghost" icon={IconClose} action={() => resetField(fieldLink.name)} />
            }
            disabled={nfts?.[category]?.attributes?.[index]?.file?.[0]}
          />
        </Field>

        <div className="flex h-full items-center">
          <Typography size="body-regular-14" className="mx-auto">
            {t('or')}
          </Typography>
        </div>

        <Field
          label={templateField?.fields.file?.label?.[language]}
          error={errors.nfts?.[category]?.attributes?.[index]?.file?.message}
        >
          <FileUploader
            id={category + '-' + index}
            label={t('create_contract.form_placeholders.upload_file')}
            accept={allowedFileType}
            defaultFiles={nfts?.[category]?.attributes?.[index]?.file}
            maxSize={5000000}
            onChange={(files) => {
              files.forEach((file) => {
                if (isFileTypeImage(file)) {
                  setValue(
                    `nfts[${category}].attributes[${index}].file`,
                    files.map((file) => renameFile(file, file.name.replace(' ', '_')))
                  );
                  if (attributes[index].type === 'partner') {
                    setValue(`nfts[${category}].attributes[${index}].link`, '');
                  } else {
                    setValue(`nfts[${category}].attributes[${index}].value`, '');
                    setValue(`nfts[${category}].attributes[${index}].link`, '');
                  }

                  setIsImageValid(true);
                } else {
                  setValue(`nfts[${category}].attributes[${index}].file`, []);
                  setIsImageValid(false);
                  toast.error(`${t('create_contract.errors.file_format')}`);
                }
              });
            }}
            onCancel={() => setValue(`nfts[${category}].attributes[${index}].file`, [])}
            ratio={['image', 'partner'].includes(attributes[index].type) ? '16:9' : 'all'}
          />
        </Field>
      </div>
    </div>
  );
};

export default DocsCustomField;
